import { useState, useContext, useRef } from 'react';
import { t } from 'i18next';
import {
  useProfileImageUpdate
} from '@sentara-health/sentara-api-hooks-core';
import { UserInitialsAvatar } from 'organisms';
import {
  ErrorMessage,
  Image,
  Loader,
  Input,
  Paragraph
} from '@sentara-health/optimahealth_web';
import { ProfileInformations } from './interface';
import { GlobalContext } from 'context';

const UserImageAvatar = ({ firstName, lastName }: ProfileInformations) => {
  
  const { loginMemberId, profileState, setProfileState, getFeature } = useContext(GlobalContext);
  const { uploadPreferenceImage } = useProfileImageUpdate(); /* profile image API to get profile image */
  const [errorMessage, setErrorMessage] =
    useState(''); /* error message state */
  const [loader, setLoader] = useState(false);
  const [inputKey, setInputKey] = useState(0); // Force re-render of input
  const fileInputRef = useRef<HTMLInputElement>(null);
  /* validate selected file size */
  const ValidateSelectedInputFile = (file: File) => {
    if (file.size > 9216983) {
      setErrorMessage(t('profile.profileImageSizeError') ?? '');
      return false;
    }
    return true;
  };

  const onImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    event?.preventDefault();
    setErrorMessage('');
    setLoader(true);
    if (event.target?.files?.length) {
      const inputFile = event.target.files[0];
      // Clear the input immediately to prevent Safari from showing the image
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      const valid = ValidateSelectedInputFile(inputFile);
      if (valid) {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = (reader.result as string).split(',')[1];
          const payload = {
            profileImage: {
              contentType: inputFile.type,
              data: base64String,
              size: inputFile.size,
              title: inputFile.name,
              creation: new Date(),
            },
            identifier: [
              {
                system: 'memberId',
                value: loginMemberId,
              },
            ],
          };
          // Force input re-render to completely reset it
          setInputKey((prev) => prev + 1);
          uploadPreferenceImage(payload)
            .then((response) => {
              if (response?.status === 201) {
                setErrorMessage('');
                setProfileState((prevState: any) => ({
                  ...prevState,
                  profileImage: base64String,
                }));
              }
            })
            .finally(() => {
              setLoader(false);
            });
        };
        reader.readAsDataURL(inputFile);
      } else {
        setInputKey((prev) => prev + 1);
        setLoader(false);
      }
    } else {
      setInputKey((prev) => prev + 1);
      setLoader(false);
    }
  };

  return (
    <div>
      <div className="flex-no-wrap align-items-center position-relative mb-4 row mt-lg-4 mt-md-4">
        {loader ? (
          <div data-TestId="loader_changeprofileimage">
            <Loader />
          </div>
        ) : (
          profileState?.profileImage && (
            <div className="col-lg-12 col-md-12 col-sm-6 d-flex col-6 justify-content-center">
              <div className="d-flex profile_image rounded-circle mx-lg-auto mx-md-auto justify-content-center">
                <Image
                  dataTestId="profileImage"
                  alttext={`${firstName} ${lastName} Profile Image`}
                  className="img-fluid d-block"
                  path={'data:image/png;base64,' + profileState?.profileImage}
                />
              </div>
            </div>
          )
        )}
        {
        profileState?.profileImage?.length <= 0 &&
        (firstName || lastName) ? (
          <div className="col-lg-12 col-md-12 col-sm-auto col-auto text-center flex-1 pt-0 pt-lg-4 pt-md-4 px-0 mt-lg-2 mt-md-2">
            <UserInitialsAvatar firstName={firstName} lastName={lastName} />
          </div>
        ) : null}
        {getFeature?.ProfileImageEdit && <div className="col-md-12 col-auto text-center flex-1 pt-0 pt-md-4 px-0 mt-md-2">
          {errorMessage && <ErrorMessage children={errorMessage} data-TestId={'error-message'} />}
          <div className="position-relative mt-2 mb-2">
            <label htmlFor="upload-photo" style={{ cursor: 'pointer' }}>
              <Paragraph
                text={t('profile.changeProfilePhoto')}
                variant="bold"
                data-TestId={'changeProfilePhoto'}
                className={'upload-photo'}
              />
            </label>
            <Input
              key={inputKey}
              ref={fileInputRef}
              data-TestId="uploadProfileImage"
              type="file"
              name="photo"
              id="upload-photo"
              onChange={onImageChange}
              accept="image/png, image/jpg, image/jpeg"
              data-focusable="false"
              role="link"
              style={{ 
                opacity: 0,
              }}
            />
          </div>
        </div>}
      </div>
    </div>
  );
};

export default UserImageAvatar;
